/**
 * InstallPrompt — invites mobile-web visitors to install the PWA to their
 * home screen on first sign-in. One component for both apps.
 *
 * Two device flavours:
 *   - Android (Chrome / Edge / Samsung Internet): captures the
 *     `beforeinstallprompt` event globally on mount, stashes the
 *     deferred event, then on user accept calls deferredEvent.prompt()
 *     to fire the OS-level install dialog. Browser does the rest.
 *   - iOS Safari: no beforeinstallprompt support. Falls back to
 *     custom instructions ("Tap Share → Add to Home Screen") with an
 *     illustrative share-icon glyph. User completes the install
 *     manually.
 *
 * Suppression matrix — the prompt is NEVER shown when any of:
 *   - Desktop (no touch / >= md viewport with mouse pointer)
 *   - Already running in PWA standalone mode (display-mode: standalone
 *     OR iOS-specific navigator.standalone)
 *   - Inside the Capacitor native wrap (window.Capacitor is present)
 *   - User dismissed within the last 30 days
 *   - User already accepted/installed the app (appinstalled event
 *     fired previously, recorded in localStorage)
 *
 * Trigger: caller passes `enabled` prop. When `enabled` flips from
 * false → true (= user just signed in), eligibility is re-checked and
 * the prompt may appear. Caller controls trigger; component owns
 * suppression + cooldown.
 *
 * Caveat: the beforeinstallprompt event is fired by the browser on
 * page load, BEFORE the user signs in. So we register the capture
 * listener immediately and hold the deferred event in component state.
 * If the user signs in within the same session, we use it; if they
 * navigate away first, the next page load gets a fresh event.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { X, Share, Plus, MoreVertical } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: ReadonlyArray<string>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt: () => Promise<void>;
}

// Module-level capture of beforeinstallprompt. The browser fires this
// event ONCE per page load, usually within a few seconds of first
// interaction — often BEFORE the user has signed in. If we registered
// the listener inside the component's useEffect, late-mounting
// InstallPrompt instances (e.g., Core's signed-in shell which only
// renders after auth) would miss the event entirely. Registering at
// module level (i.e., as soon as the bundle parses) means we stash
// the deferred event no matter when the InstallPrompt component mounts.
//
// Listeners is a Set so multiple <InstallPrompt /> instances (Fitting
// + Core mounted simultaneously, which doesn't happen in practice but
// would work) can subscribe.
let moduleDeferredEvent: BeforeInstallPromptEvent | null = null;
const moduleListeners = new Set<(e: BeforeInstallPromptEvent | null) => void>();
let moduleListenerInstalled = false;

function installModuleListener() {
  if (typeof window === 'undefined' || moduleListenerInstalled) return;
  moduleListenerInstalled = true;
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    moduleDeferredEvent = e as BeforeInstallPromptEvent;
    moduleListeners.forEach((fn) => fn(moduleDeferredEvent));
  });
  window.addEventListener('appinstalled', () => {
    moduleDeferredEvent = null;
    moduleListeners.forEach((fn) => fn(null));
  });
}

// Side-effect: install the listener as soon as the module loads. Safe
// to run multiple times — the flag prevents duplicate listeners.
installModuleListener();

interface InstallPromptProps {
  /** True once the user is signed in. Flipping false→true is the trigger. */
  enabled: boolean;
  /** Display name used in the prompt copy. */
  appName?: string;
  /** Unique key per app so Core + Fitting have independent cooldowns. */
  storageKey?: string;
}

type StoredState = {
  /** Epoch ms of last shown (used to compute 30-day cooldown). */
  lastShownAt?: number;
  /** Permanently suppress — user installed or asked to never see again. */
  installed?: boolean;
};

const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function getStoredState(storageKey: string): StoredState {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    return JSON.parse(raw) as StoredState;
  } catch {
    return {};
  }
}

function setStoredState(storageKey: string, state: StoredState): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    // Storage quota / private mode — silently drop. The worst case is
    // we re-show next sign-in.
  }
}

/** Is the page running inside the Capacitor native wrap? */
function isCapacitorNative(): boolean {
  if (typeof window === 'undefined') return false;
  // Capacitor injects window.Capacitor with an isNativePlatform() helper
  // when it's running. On the web it's undefined.
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return !!cap?.isNativePlatform?.();
}

/** Is the page already running as a PWA (post-install)? */
function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)')?.matches) return true;
  // iOS legacy / current Safari uses navigator.standalone instead of
  // the display-mode media query.
  const nav = window.navigator as unknown as { standalone?: boolean };
  return !!nav.standalone;
}

/** Heuristic: is this a mobile/tablet device that should show the install prompt? */
function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  const coarse = window.matchMedia?.('(pointer: coarse)')?.matches;
  const hoverNone = window.matchMedia?.('(hover: none)')?.matches;
  // Touch-primary tablets (e.g. Android landscape) often exceed 1024px.
  // coarse + no-hover is the strongest signal without excluding them.
  if (coarse && hoverNone) return true;
  // Fallback for UAs that don't report hover reliably.
  return !!coarse && window.innerWidth < 1280;
}

/** UA-based iOS detection — iPad Safari with desktop-mode returns false here, which is fine (it'd show no prompt). */
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ reports itself as Mac with touch support.
    (navigator.platform === 'MacIntel' && (navigator as unknown as { maxTouchPoints?: number }).maxTouchPoints! > 1);
}

export function InstallPrompt({
  enabled,
  appName = 'GolfAuth',
  storageKey = 'golfauth-install-prompt',
}: InstallPromptProps) {
  const [open, setOpen] = useState(false);
  // Initialised from the module-level captured event. If
  // beforeinstallprompt already fired before this component mounted,
  // we pick it up immediately. Subsequent firings (rare — usually
  // one per page load) flow through the moduleListeners Set below.
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(
    moduleDeferredEvent,
  );
  const enabledPrev = useRef(false);
  // Set when the user signs in this session but beforeinstallprompt
  // hasn't fired yet — re-evaluate when the deferred event arrives.
  const pendingAfterSignIn = useRef(false);

  const tryOpenPrompt = useCallback(() => {
    if (!enabled) return false;
    if (isCapacitorNative()) return false;
    if (isStandaloneDisplay()) return false;
    if (!isMobileDevice()) return false;

    const stored = getStoredState(storageKey);
    if (stored.installed) return false;
    if (stored.lastShownAt && Date.now() - stored.lastShownAt < COOLDOWN_MS) return false;

    setOpen(true);
    setStoredState(storageKey, { ...stored, lastShownAt: Date.now() });
    return true;
  }, [enabled, storageKey]);

  // Subscribe to module-level event updates. Also handles the
  // appinstalled-cleared case (event passed as null).
  useEffect(() => {
    const onModule = (e: BeforeInstallPromptEvent | null) => {
      setDeferredEvent(e);
      if (e === null) {
        // appinstalled fired — mark permanent install + close any
        // open prompt.
        setStoredState(storageKey, { installed: true });
        setOpen(false);
      }
    };
    moduleListeners.add(onModule);
    return () => {
      moduleListeners.delete(onModule);
    };
  }, [storageKey]);

  // Show on sign-in, or when beforeinstallprompt arrives after sign-in.
  // Android without a deferred event still opens — manual Chrome steps.
  useEffect(() => {
    const justEnabled = enabled && !enabledPrev.current;
    enabledPrev.current = enabled;

    if (!enabled) {
      pendingAfterSignIn.current = false;
      return;
    }

    if (justEnabled) {
      pendingAfterSignIn.current = true;
    }

    const deferredArrivedAfterSignIn =
      pendingAfterSignIn.current && !!deferredEvent;
    if (!justEnabled && !deferredArrivedAfterSignIn) return;

    if (tryOpenPrompt()) {
      pendingAfterSignIn.current = false;
    }
  }, [enabled, deferredEvent, tryOpenPrompt]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleInstall = async () => {
    if (deferredEvent) {
      try {
        await deferredEvent.prompt();
        const { outcome } = await deferredEvent.userChoice;
        if (outcome === 'accepted') {
          // appinstalled handler will mark installed=true; defensive
          // write here in case the event doesn't fire (older Edge).
          setStoredState(storageKey, { installed: true });
        }
      } catch {
        // User dismissed the OS dialog or it errored — leave the
        // cooldown timestamp we already set and close.
      }
      setOpen(false);
      setDeferredEvent(null);
    }
    // iOS: there's no programmatic install — the modal stays as
    // instructions until the user closes it.
  };

  if (!open) return null;

  const ios = isIOS();
  const androidManualInstall = !ios && !deferredEvent;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="install-prompt-title"
      onClick={handleClose}
    >
      <div
        className="w-full sm:max-w-sm bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl shadow-2xl p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <img
              src="/apple-touch-icon.png"
              alt={`${appName} app icon`}
              className="w-12 h-12 rounded-xl shadow-sm"
            />
            <div>
              <h2
                id="install-prompt-title"
                className="text-base font-semibold text-gray-900 dark:text-white"
              >
                Add {appName} to your home screen
              </h2>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                Faster access. Works offline. No app store.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 -mr-1"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {ios ? (
          <>
            {/* iOS Safari has no programmatic install. Show step-by-step
                instructions with the share-glyph users will tap. */}
            <ol className="space-y-3 mb-5 text-sm text-gray-700 dark:text-slate-300">
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-xs font-semibold flex items-center justify-center">
                  1
                </span>
                <span className="flex items-center gap-2">
                  Tap the
                  <Share className="inline-block w-4 h-4 text-brand-600 dark:text-brand-400" aria-label="Share" />
                  Share button at the bottom of Safari
                </span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-xs font-semibold flex items-center justify-center">
                  2
                </span>
                <span className="flex items-center gap-2 flex-wrap">
                  Choose
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 dark:bg-slate-800 rounded text-xs font-medium">
                    <Plus className="w-3 h-3" />
                    Add to Home Screen
                  </span>
                </span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-xs font-semibold flex items-center justify-center">
                  3
                </span>
                <span>Tap <span className="font-medium">Add</span> in the top-right corner.</span>
              </li>
            </ol>
            <button
              type="button"
              onClick={handleClose}
              className="w-full py-3 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-medium transition-colors"
            >
              Got it
            </button>
          </>
        ) : androidManualInstall ? (
          <>
            <ol className="space-y-3 mb-5 text-sm text-gray-700 dark:text-slate-300">
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-xs font-semibold flex items-center justify-center">
                  1
                </span>
                <span className="flex items-center gap-2 flex-wrap">
                  Tap the
                  <MoreVertical className="inline-block w-4 h-4 text-brand-600 dark:text-brand-400" aria-label="Menu" />
                  menu in Chrome (top-right)
                </span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-xs font-semibold flex items-center justify-center">
                  2
                </span>
                <span className="flex items-center gap-2 flex-wrap">
                  Choose
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 dark:bg-slate-800 rounded text-xs font-medium">
                    Install app
                  </span>
                  or
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 dark:bg-slate-800 rounded text-xs font-medium">
                    Add to Home screen
                  </span>
                </span>
              </li>
              <li className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-xs font-semibold flex items-center justify-center">
                  3
                </span>
                <span>Confirm when prompted.</span>
              </li>
            </ol>
            <button
              type="button"
              onClick={handleClose}
              className="w-full py-3 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-medium transition-colors"
            >
              Got it
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-5">
              Install {appName} as an app on your phone. You'll get an icon on your home screen
              and a full-screen experience without browser chrome.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-3 rounded-lg border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-300 font-medium hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={handleInstall}
                className="flex-1 py-3 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-medium transition-colors"
              >
                Install
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
